import { Stack, Typography } from '@mui/material';
import { AxiosError } from 'axios';
import BackToSchoolsAction from './actions/BackToSchoolsAction';

type ItemDetailsUnavailableType = {
  /** Lower-case noun used in the messages, e.g. 'activity' or 'unit' */
  itemName: string;
  error: unknown;
};

const getMessage = (itemName: string, error: unknown) => {
  const status = error instanceof AxiosError ? error.response?.status : undefined;
  if (status === 403) {
    return {
      title: `You do not have access to this ${itemName}`,
      detail: `This ${itemName} is part of content that is not included in your account. Please contact your administrator if you believe you should have access.`,
    };
  }
  if (status === 404 || status === 400) {
    return {
      title: `This ${itemName} could not be found`,
      detail: 'It may have been removed or the link may be out of date.',
    };
  }
  return {
    title: `This ${itemName} could not be loaded`,
    detail: `Something went wrong while loading the ${itemName}. Please try again later.`,
  };
};

/**
 * Shown in place of a detail plan page when the item cannot be loaded,
 * for example when a shared link points at content the user is not entitled to.
 */
export default function ItemDetailsUnavailable({ itemName, error }: ItemDetailsUnavailableType) {
  const { title, detail } = getMessage(itemName, error);
  return (
    <Stack paddingX="5%" paddingTop={3} maxWidth="1400px" marginX="auto" data-test={`${itemName}-detail-unavailable`}>
      <BackToSchoolsAction sx={{ alignSelf: 'flex-start', paddingX: 0, marginBottom: 1, fontWeight: 500 }} />
      <Stack alignItems="center" paddingTop={10} gap={1} textAlign="center">
        <Typography variant="h5" component="h1" data-test="item-detail-unavailable-title">
          {title}
        </Typography>
        <Typography variant="body1" maxWidth="600px" data-test="item-detail-unavailable-detail">
          {detail}
        </Typography>
      </Stack>
    </Stack>
  );
}
